import numeral from 'numeral';
import moment from 'moment';
import _ from 'lodash';

import config from '../config/config';

const smallAgreement = (agreement) => {
  return {
    accountNameDisplay: agreement.accountNameDisplay,
    accountNumberDisplay: agreement.accountNumberDisplay,
    checkSum: agreement.checkSum,
    balance: agreement.balance,
    ytdRMDDistributions: agreement.distributionYTD,
    remainingRMDForYear: agreement.remainingRMDForYear,
    ytdTaxWithholdings: agreement.taxWithHoldingsYTD,
    rmdamount: agreement.rmdamount,
  };
};

export const orderProductType = (type) => {
  const _ProductTypeOrder = {
    MUTUAL_FUND: 1,
    ACTIVE_PLUS: 2,
    MANAGED_ACCOUNT: 3,
    BROKERAGE: 4,
    BROKERAGE_ADVANTAGE: 5,
    RETIREMENT_PLAN: 6,
    RETAIL_TRAC: 7,
    WORKPLACE_RETIREMENT: 8,
    RPS_TRAC: 9,
  };

  if (_ProductTypeOrder[type]) {
    return _ProductTypeOrder[type];
  } else {
    return 10;
  }
};

export const roundTo100th = (value) => {
  const newValue = value || 0.0; // in case the value is null

  return Math.round(100 * newValue) / 100;
};

export const currency = (value) => {
  return typeof value !== 'number' || isNaN(value)
    ? '$0.00'
    : numeral(value).format('$0,0.00');
};

export const cents = (value) => {
  return currency(value).split('.')[1];
};

export const dollars = (value) => {
  return currency(value).split('.')[0].substring(1);
};

export const myAccountsUrl = () => config.url + '/usis/my-accounts/dashboard';

export const firstBusinessDay = (currentYear) => {
  // input: current year.
  // output: last day of of previous year.

  let firstDayOfYear = currentYear + '/01/01';
  return moment(firstDayOfYear).format('MM/DD/YYYY');
};

export const lastBusinessDay = (currentYear) => {
  // input: current year.
  // output: last day of of previous year.

  let lastYear = currentYear - 1 + '/12/31';
  return moment(lastYear).format('MMM D, YYYY');
};

export const lastBusinessDayNumeric = (currentYear) => {
  // input: current year.
  // output: last day of of previous year.

  let lastYear = currentYear - 1 + '/12/31';
  return moment(new Date(lastYear)).format('MM/DD/YYYY');
};

export const lastBusinessDateNumeric = (date) => {
  const d = moment(date);
  return d.tz('America/New_York').format('MM/DD/YYYY');
};

const createAgreementType = (agreement) => {
  const { agreementType, balance, rmdamount } = agreement;

  return {
    type: agreementType,
    totalBalance: balance,
    totalRMD: rmdamount,
    agreements: [smallAgreement(agreement)],
  };
};

const createProductType = (agreement) => {
  let { productType } = agreement;

  return {
    type: productType,
    orderByType: orderProductType(productType),
    agreementTypes: [createAgreementType(agreement)],
  };
};

export const mapAgreementType = (type) => {
  const _agreementTypes = {
    TRADITIONAL_IRA: 'Traditional IRA',
    ROLLOVER_IRA: 'Rollover IRA',
    SEP_IRA: 'SEP IRA',
    PROFIT_SHARING_PLAN: 'Profit Sharing Plan',
    MONEY_PURCHASE_PENSION_PLAN: 'Money Purchase Pension Plan',
    RETIREMENT_PLAN_403: '403(b)',
    INDIVIDUAL_401K: 'Individual 401(k)',
    SIMPLE_IRA: 'Simple IRA',
    SAR_SEP: 'SAR-SEP',
    RETIREMENT_PLAN_401K: '401(k)',
    RETIREMENT_PLAN_457: '457',
    KEOGH: 'KEOGH',
  };

  if (_agreementTypes[type]) {
    return _agreementTypes[type];
  } else {
    return null;
  }
};

export const accountNumber = (account) => {
  let finalAccountNo;
  if (account) {
    finalAccountNo =
      account.accountNumberDisplay +
      `${_.isNumber(account.checkSum) ? '-' + account.checkSum : ''}`;
  }
  return finalAccountNo;
};

const updateProductType = (productTypeElem, agreement) => {
  const {
    agreementType,
    rmdamount,
    balance,
    distributionYTD,
    remainingRMDForYear,
    taxWithHoldingsYTD,
  } = agreement;
  let agreementTypeElem = productTypeElem.agreementTypes.find(
    (elem) => elem.type === agreementType
  );

  // Existing agreement type
  if (agreementTypeElem) {
    agreementTypeElem.totalRMD += rmdamount;
    agreementTypeElem.totalBalance += balance;
    agreementTypeElem.agreements.push(smallAgreement(agreement));
    //add 3 new fields for summation
    if (distributionYTD) {
      if (!agreementTypeElem.distributionYTD) {
        agreementTypeElem.distributionYTD = 0;
      }
      agreementTypeElem.distributionYTD += distributionYTD;
    }
    if (remainingRMDForYear) {
      if (!agreementTypeElem.remainingRMDForYear) {
        agreementTypeElem.remainingRMDForYear = 0;
      }
      agreementTypeElem.remainingRMDForYear +=
        roundTo100th(remainingRMDForYear);
    }
    if (taxWithHoldingsYTD) {
      if (!agreementTypeElem.taxWithHoldingsYTD) {
        agreementTypeElem.taxWithHoldingsYTD = 0;
      }
      agreementTypeElem.taxWithHoldingsYTD += taxWithHoldingsYTD;
    }
  }
  // New agreement type
  else {
    productTypeElem.agreementTypes.push(createAgreementType(agreement));
  }
};

const createPagination = (plans) => {
  let rowTreshold = 9; // Business requirement
  let plansCount = 0;
  let agreementTypesCount = 0;
  let agreementsCount = 0;

  const paginationObj = {
    isCollapsed: false,
    totalLength: 0,
    remainingLength: 0,
  };

  const pagination = Object.assign({}, paginationObj);

  // Update counts dynamically
  plans.forEach((plan) => {
    plansCount++;
    agreementTypesCount += plan.agreementTypeContainers.length;
    plan.agreementTypeContainers.forEach((agreements) => {
      agreementsCount += agreements.rmdAgreements.length;
    });
  });
  pagination.totalLength = plansCount + agreementTypesCount + agreementsCount;

  if (pagination.totalLength > rowTreshold) {
    pagination.isCollapsed = true;
    pagination.remainingLength = pagination.totalLength - rowTreshold;
  }

  return pagination;
};

export const groupAgreementsByProductTypeAndAgreementType = (agreements) => {
  if (!agreements) {
    return [];
  }
  return agreements.reduce(
    (accumulator, currentAgreement) => {
      const { productType } = currentAgreement;
      let productTypeElem = accumulator.productTypes.find(
        (elem) => elem.type === productType
      );

      // Existing product type
      if (productTypeElem) {
        updateProductType(productTypeElem, currentAgreement);
      }
      // New Product Type
      else {
        accumulator.productTypes.push(createProductType(currentAgreement));
      }

      return accumulator;
    },
    { productTypes: [] }
  );
};

export const roundBalancesAndRmdAmounts = (agreements) => {
  if (!agreements) {
    return [];
  }

  return agreements.map((agreement) => {
    agreement.balance = roundTo100th(agreement.balance);
    agreement.rmdamount = roundTo100th(agreement.rmdamount);

    return agreement;
  });
};

// Pagination
export const tablePagination = (plans) => {
  if (plans) {
    return createPagination(plans);
  }

  // return agreements.map(agreement => {
  // 	agreement.balance = roundTo100th(agreement.balance);
  // 	agreement.rmdamount = roundTo100th(agreement.rmdamount);

  // 	return agreement;
  // });
};
